import { Input, SelectFieldOption } from "components/Form/FormikControls";
import { useField } from "formik";
import { useEffect } from "react";
import React from "react";
import { useSelector } from "react-redux";
import { getBusinessById } from "selectors";
import { TableCell, TableRow } from "@material-ui/core";
import styled from "styled-components/macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSideLoadedBusinessMappableOptionsById,
  getSideLoadedBusinessById,
} from "selectors/sideLoadedBusinesses";
import { SelectField } from "components/Form/FormikControls";
import { useState } from "react";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FieldConfiguration } from "./fieldConfigurations";
import { Row } from "components/Layout";
import {
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "selectors/indexes";

import { Tooltip } from "components/Form/FormikControls/Tooltip";

export interface SideLoadedFieldRowProps {
  configuration: FieldConfiguration;
  businessId?: string;
  recordId: string;
}

const TableRowStatus = {
  ValidAndMapped: "ValidAndMapped",
  InvalidAndMapped: "InvalidAndMapped",
  UnMapped: "UnMapped",
};

export const ActiveTableRow = styled(TableRow)`
  background-color: ${({ status, theme }) => {
    switch (status) {
      case TableRowStatus.ValidAndMapped:
        return theme.colors.lightGreen;
      case TableRowStatus.InvalidAndMapped:
        return theme.colors.error;
      case TableRowStatus.UnMapped:
        return theme.colors.gray85;
    }
  }};
`;

interface MappableFieldSelectorProps {
  name: string;
  recordId: string;
}

const OptionHeading = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
  color: ${({ theme }) => theme.colors.titleLight};
  text-transform: uppercase;
`;

function formatOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <div>{option.label}</div>
    </div>
  );
}

function MappableFieldSelector({ name, recordId }: MappableFieldSelectorProps) {
  const options = useSelector(
    getSideLoadedBusinessMappableOptionsById(recordId),
  );
  return (
    <SelectField
      name={name}
      options={options}
      placeholder="Not mapped..."
      isClearable
      formatOptionLabel={formatOptionLabel}
    />
  );
}

function BusinessLookup({ valueFieldName }) {
  const valueField = useField(valueFieldName)[0];

  const businessIdByAgentCodeLookup = useSelector(
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );
  const businessIdBySaleyardCodeLookup = useSelector(
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );

  // Try and pull it out of the lookup.
  const businessIdFromAgentCode = businessIdByAgentCodeLookup[valueField.value];
  const businessIdFromSaleyardCode =
    businessIdBySaleyardCodeLookup[valueField.value];

  const businessId = businessIdFromAgentCode || businessIdFromSaleyardCode;

  const mappedBusiness = useSelector(getBusinessById(businessId)) || null;

  if (!mappedBusiness) {
    return (
      <div>
        No business found for {valueField.value}.{" "}
        <Tooltip title="This value needs to map to the Agent Code or Saleyard Code of an existing business - does a business with that code exist?" />
      </div>
    );
  }

  return (
    <div>
      {mappedBusiness.name}{" "}
      <Tooltip
        title={`Found business from ${businessIdFromAgentCode ? "Agent Code" : "Saleyard Code"}`}
      />
    </div>
  );
}

function MappableFieldValueDisplay({
  mappableFieldValue,
  valueFieldName,
  isBusinessLookup,
}: {
  mappableFieldValue: string;
  valueFieldName: string;
  isBusinessLookup?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const valueField = useField(valueFieldName)[0];

  if (mappableFieldValue === null) {
    return null;
  }

  if (isBusinessLookup) {
    return <BusinessLookup valueFieldName={valueFieldName} />;
  }

  if (isEditing) {
    return <Input name={valueFieldName} disableAutoComplete />;
  }

  return (
    <span onClick={() => setIsEditing(true)} className="cursor-pointer">
      <FontAwesomeIcon icon={faPencil} />
      &nbsp;{valueField.value}
    </span>
  );
}

export function SideLoadedFieldRow({
  configuration,
  businessId,
  recordId,
}: SideLoadedFieldRowProps) {
  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const valueFieldName = `Value__${configuration.value}`;
  const mappableFieldName = `MappableField__${configuration.value}`;

  const valueFieldElements = useField(valueFieldName);
  const valueMeta = valueFieldElements[1];
  const valueFieldHelpers = valueFieldElements[2];
  const mappableFieldField = useField(mappableFieldName)[0];
  const mappableFieldValue = mappableFieldField.value;

  const setValueFieldValue = valueFieldHelpers.setValue;

  let rowStatus = TableRowStatus.UnMapped;
  if (mappableFieldField.value !== null) {
    if (valueMeta.error) {
      rowStatus = TableRowStatus.InvalidAndMapped;
    } else {
      rowStatus = TableRowStatus.ValidAndMapped;
    }
  }

  useEffect(() => {
    if (mappableFieldValue === null) {
      setValueFieldValue("");
    } else {
      setValueFieldValue(sideLoadedBusiness.rawData[mappableFieldValue]);
    }
  }, [mappableFieldValue, sideLoadedBusiness, setValueFieldValue]);

  const BusinessValueRenderer = configuration.businessValueRenderer || null;
  const existingValue = BusinessValueRenderer ? (
    <BusinessValueRenderer businessId={businessId} />
  ) : null;
  return (
    <>
      {configuration.group && (
        <TableRow>
          <TableCell colSpan={2}>
            <Row marginLeft={4}>
              <h2>{configuration.group}</h2>
            </Row>
          </TableCell>
        </TableRow>
      )}

      <ActiveTableRow status={rowStatus}>
        <TableCell>
          <strong>{configuration.label}</strong>
          <br />
          <i>{existingValue}</i>
        </TableCell>
        <TableCell>
          <MappableFieldSelector name={mappableFieldName} recordId={recordId} />
          <MappableFieldValueDisplay
            mappableFieldValue={mappableFieldValue}
            valueFieldName={valueFieldName}
            isBusinessLookup={configuration.isBusinessLookup}
          />
        </TableCell>
      </ActiveTableRow>
    </>
  );
}
